import type { FinanceFormField } from './financeSolicitacoesConfig'
import { mapaCargosSelectOptions } from '@/components/Configuracao/mapaCargos/mapaCargosUtils'

const urgencyOptions = [
  { value: 'urgente', label: 'Urgente' },
  { value: 'alta', label: 'Alta' },
  { value: 'media', label: 'Média' },
  { value: 'baixa', label: 'Baixa' },
]

const commonEmpresaFields: FinanceFormField[] = [
  { name: 'cnpj', label: 'CNPJ', type: 'text', required: true },
  { name: 'razaoSocial', label: 'Razão social', type: 'text', required: true },
]

const areaPrestacaoOptions = [
  { value: 'comercial', label: 'Comercial' },
  { value: 'suporte_evolucao', label: 'Suporte & evolução' },
  { value: 'marketing_digital', label: 'Marketing Digital' },
  { value: 'implantacao_projetos', label: 'Implantação & projetos' },
  { value: 'administrativo', label: 'Administrativo' },
  { value: 'criacao', label: 'Criação' },
  { value: 'financeiro', label: 'Financeiro' },
  { value: 'configuracao', label: 'Configuração' },
]

export const EXTENDED_PORTAL_TYPE_KEYS = [
  'C0_NOVO_CLIENTE',
  'P2_ATUALIZACAO_CONTRATO',
  'P3_ENTRADA_PARCEIRO',
  'P4_AREA_COMERCIAL',
  'P6_NOVA',
  'P8_ENCERRAMENTO',
  'P9_2_VIA',
  'C1_ATUALIZACAO_CADASTRO',
  'C5_ENCERRAMENTO',
  'G0_GESTAO_GERAL',
  'G1_ENTRADA_CONTRATO',
  'G2_ATUALIZACAO',
  'G3_2_VIA',
  'G4_ENCERRAMENTO',
  'G5_CONTRATACAO_PJ',
  'S1_PROCESSOS',
  'S2_ENCERRAMENTO',
  'S3_2_VIA',
  'S4_SOFTWARE',
  'S5_SUPORTE',
  'C4_CONTRATO_AGRADA',
  'O1_OUTROS_COMERCIAL',
] as const

export type ExtendedPortalTypeKey = (typeof EXTENDED_PORTAL_TYPE_KEYS)[number]

export const EXTENDED_FORM_FIELDS: Record<ExtendedPortalTypeKey, FinanceFormField[]> = {
  C0_NOVO_CLIENTE: [
    ...commonEmpresaFields,
    { name: 'parceiro', label: 'Parceiro de origem', type: 'text' },
    { name: 'descricao', label: 'Descrição do negócio', type: 'textarea', required: true },
    { name: 'valor', label: 'Valor estimado (R$)', type: 'number' },
    { name: 'respFinanceiroEmail', label: 'E-mail financeiro', type: 'text', required: true },
    { name: 'anexo', label: 'Proposta ou documentos', type: 'file' },
  ],
  P2_ATUALIZACAO_CONTRATO: [
    { name: 'resumo', label: 'Resumo da solicitação', type: 'text', required: true },
    { name: 'alteracoes', label: 'O que precisa ser atualizado?', type: 'textarea', required: true },
    { name: 'urgencia', label: 'Nível de urgência', type: 'select', required: true, options: urgencyOptions },
    { name: 'dataConclusao', label: 'Data desejada para conclusão', type: 'date', required: true },
    { name: 'areaPrestacao', label: 'Área da prestação de serviço', type: 'text', required: true },
    { name: 'responsavelAprovacao', label: 'Responsável pela aprovação', type: 'text', required: true },
  ],
  P3_ENTRADA_PARCEIRO: [
    { name: 'resumo', label: 'Resumo do período de descanso', type: 'text', required: true },
    {
      name: 'acordoResponsavel',
      label: 'O que já foi acordado com a liderança focal?',
      type: 'textarea',
      required: true,
    },
    { name: 'responsavelAprovacao', label: 'Responsável pela aprovação', type: 'text', required: true },
    {
      name: 'areaPrestacao',
      label: 'Área da prestação de serviço',
      type: 'select',
      required: true,
      options: areaPrestacaoOptions,
    },
    { name: 'dataInicioPausa', label: 'Data de início da pausa', type: 'date', required: true },
    { name: 'dataRetornoPrevisto', label: 'Data de retorno previsto', type: 'date', required: true },
    { name: 'diasCorridos', label: 'Dias corridos de descanso', type: 'number', required: true },
    {
      name: 'mesesParceria',
      label: 'Meses de parceria (elegibilidade)',
      type: 'number',
      required: true,
      placeholder: 'Mínimo 12 para descanso',
    },
    {
      name: 'nfsEmitidas',
      label: 'NFs emitidas (elegibilidade)',
      type: 'number',
      required: true,
      placeholder: 'Mínimo 12 para descanso',
    },
    {
      name: 'declaracaoCiente',
      label: 'Declaro ciência de que o período é descanso remunerado acordado',
      type: 'select',
      required: true,
      options: [{ value: 'ciente', label: 'Ciente' }],
    },
  ],
  P4_AREA_COMERCIAL: [
    { name: 'nomeCompleto', label: 'Nome completo', type: 'text', required: true },
    { name: 'emailProfissional', label: 'E-mail profissional', type: 'text', required: true },
    {
      name: 'areaPrestacao',
      label: 'Área da prestação de serviço',
      type: 'select',
      required: true,
      options: areaPrestacaoOptions,
    },
    { name: 'dataAtivacao', label: 'Data desejada de ativação', type: 'date', required: true },
    {
      name: 'beneficio',
      label: 'Serviço desejado',
      type: 'select',
      required: true,
      options: [
        { value: 'psicologo', label: 'Psicólogo' },
        { value: 'nutricionista', label: 'Nutricionista' },
      ],
    },
  ],
  P6_NOVA: [
    { name: 'resumo', label: 'Resumo da ausência', type: 'text', required: true },
    { name: 'periodoInicio', label: 'Início da ausência', type: 'date', required: true },
    { name: 'periodoFim', label: 'Fim da ausência', type: 'date', required: true },
    { name: 'motivo', label: 'Motivo', type: 'textarea', required: true },
  ],
  P8_ENCERRAMENTO: [
    { name: 'resumo', label: 'Nome completo', type: 'text', required: true },
    {
      name: 'areaPrestacao',
      label: 'Área da prestação de serviço',
      type: 'select',
      required: true,
      options: areaPrestacaoOptions,
    },
    { name: 'valorAntecipar', label: 'Valor a antecipar (R$)', type: 'number', required: true },
    { name: 'dataLimite', label: 'Data limite desejada', type: 'date', required: true },
    {
      name: 'avisoLegalAceite',
      label: 'Aceito que a antecipação é pontual e conforme contrato',
      type: 'select',
      required: true,
      options: [{ value: 'sim', label: 'Sim, li e aceito' }],
    },
  ],
  P9_2_VIA: [
    { name: 'resumo', label: 'Resumo da ideia', type: 'text', required: true },
    { name: 'detalhesMelhoria', label: 'Detalhes da melhoria sugerida', type: 'textarea', required: true },
    {
      name: 'equipe',
      label: 'Equipe relacionada',
      type: 'select',
      required: true,
      options: areaPrestacaoOptions,
    },
    {
      name: 'anexosReferencia',
      label: 'Anexos (prints, documentos)',
      type: 'file',
    },
  ],
  G1_ENTRADA_CONTRATO: [
    { name: 'destino', label: 'Destino', type: 'text', required: true },
    { name: 'dataIda', label: 'Data de ida', type: 'date', required: true },
    { name: 'dataVolta', label: 'Data de volta', type: 'date', required: true },
    { name: 'motivo', label: 'Motivo da viagem', type: 'textarea', required: true },
    { name: 'hospedagem', label: 'Hospedagem necessária?', type: 'select', options: [
      { value: 'sim', label: 'Sim' },
      { value: 'nao', label: 'Não' },
    ]},
    { name: 'passagens', label: 'Passagens aéreas?', type: 'select', options: [
      { value: 'sim', label: 'Sim' },
      { value: 'nao', label: 'Não' },
    ]},
    { name: 'valorEstimado', label: 'Valor estimado (R$)', type: 'number', required: true },
    { name: 'observacoes', label: 'Observações', type: 'textarea' },
  ],
  G0_GESTAO_GERAL: [
    { name: 'resumo', label: 'Resumo da demanda', type: 'text', required: true },
    { name: 'descricao', label: 'Descrição detalhada', type: 'textarea', required: true },
    { name: 'urgencia', label: 'Urgência', type: 'select', required: true, options: urgencyOptions },
    { name: 'anexo', label: 'Anexos (opcional)', type: 'file' },
  ],
  C1_ATUALIZACAO_CADASTRO: [
    ...commonEmpresaFields,
    {
      name: 'tipoContratacao',
      label: 'Tipo de contratação',
      type: 'select',
      required: true,
      options: [
        { value: 'novo_produto', label: 'Novo produto / serviço' },
        { value: 'expansao', label: 'Expansão de escopo' },
        { value: 'app_custom', label: 'App / customização' },
      ],
    },
    { name: 'nomeProjeto', label: 'Nome do projeto / loja', type: 'text', required: true },
    { name: 'descricao', label: 'Descrição do escopo', type: 'textarea', required: true },
    { name: 'produtosDetalhe', label: 'Produtos e quantidades', type: 'textarea', required: true },
    { name: 'infoFinanceiras', label: 'Informações financeiras', type: 'text', required: true },
    { name: 'plataforma', label: 'Plataforma', type: 'text', required: true },
    { name: 'servicos', label: 'Serviços contratados', type: 'text', required: true },
    { name: 'parceiroInterno', label: 'Parceiro interno (se aplicável)', type: 'text' },
    { name: 'dataInicioDesejada', label: 'Data desejada de início', type: 'date' },
    { name: 'respFinanceiroNome', label: 'Responsável financeiro', type: 'text', required: true },
    { name: 'respAssinaturaEmail', label: 'E-mail responsável assinatura', type: 'text', required: true },
    { name: 'anexo', label: 'Anexos (proposta, contrato)', type: 'file' },
  ],
  C5_ENCERRAMENTO: [
    ...commonEmpresaFields,
    { name: 'parceiro', label: 'Parceiro de origem', type: 'text', required: true },
    { name: 'tipoParceiro', label: 'Tipo de parceiro', type: 'select', required: true, options: [
      { value: 'agencia', label: 'Agência' },
      { value: 'integrador', label: 'Integrador' },
      { value: 'revenda', label: 'Revenda' },
      { value: 'outro', label: 'Outro' },
    ]},
    { name: 'contatoParceiro', label: 'Contato no parceiro', type: 'text', required: true },
    { name: 'descricao', label: 'Descrição do contrato', type: 'textarea', required: true },
    { name: 'valor', label: 'Valor (R$)', type: 'number', required: true },
    { name: 'prazoAtivacao', label: 'Prazo desejado de ativação', type: 'date', required: true },
    { name: 'respFinanceiroEmail', label: 'E-mail financeiro', type: 'text', required: true },
    {
      name: 'anexo',
      label: 'Anexos (proposta, docs do cliente)',
      type: 'file',
      required: true,
    },
  ],
  G2_ATUALIZACAO: [
    { name: 'colaborador', label: 'Nome completo do colaborador', type: 'text', required: true },
    { name: 'descricao', label: 'Descrição do desligamento', type: 'textarea', required: true },
    { name: 'motivo', label: 'Motivo de desligamento', type: 'text', required: true },
    { name: 'ultimoDia', label: 'Último dia de trabalho', type: 'date', required: true },
    { name: 'sistemasBloqueio', label: 'Sistemas para bloqueio', type: 'text', required: true },
    { name: 'anexo', label: 'Documentos de apoio', type: 'file' },
  ],
  G3_2_VIA: [
    { name: 'solicitante', label: 'Solicitante', type: 'text', required: true },
    { name: 'sistema', label: 'Sistema / ferramenta', type: 'text', required: true },
    { name: 'tipoAcesso', label: 'Tipo de acesso', type: 'select', required: true, options: [
      { value: 'novo', label: 'Novo acesso' },
      { value: 'revogar', label: 'Revogar acesso' },
      { value: 'alterar', label: 'Alterar permissões' },
    ]},
    { name: 'justificativa', label: 'Justificativa', type: 'textarea', required: true },
    { name: 'anexo', label: 'Prints ou credenciais (opcional)', type: 'file' },
  ],
  G4_ENCERRAMENTO: [
    { name: 'resumo', label: 'Resumo da automação', type: 'text', required: true },
    { name: 'sistemas', label: 'Sistemas envolvidos', type: 'text', required: true },
    { name: 'descricao', label: 'Descrição da demanda', type: 'textarea', required: true },
    { name: 'prazo', label: 'Prazo desejado', type: 'date' },
    { name: 'anexo', label: 'Exemplo / mockup (opcional)', type: 'file' },
  ],
  G5_CONTRATACAO_PJ: [
    { name: 'nomeCompleto', label: 'Nome completo do profissional', type: 'text', required: true },
    { name: 'emailProfissional', label: 'E-mail profissional', type: 'text', required: true },
    { name: 'telefone', label: 'Telefone / WhatsApp', type: 'text' },
    { name: 'cpfCnpj', label: 'CPF ou CNPJ (se já tiver)', type: 'text' },
    {
      name: 'areaAtuacao',
      label: 'Área / setor de atuação',
      type: 'select',
      required: true,
      options: areaPrestacaoOptions,
    },
    {
      name: 'mapaCargoId',
      label: 'Cargo',
      type: 'select',
      required: true,
      options: mapaCargosSelectOptions,
    },
    { name: 'remuneracaoMensal', label: 'Remuneração mensal prevista (R$)', type: 'number', required: true },
    { name: 'dataInicioPrevista', label: 'Data prevista de início', type: 'date', required: true },
    { name: 'gestorResponsavel', label: 'Gestor / liderança focal responsável', type: 'text', required: true },
    { name: 'descricaoEscopo', label: 'Descrição do escopo e atividades', type: 'textarea', required: true },
    {
      name: 'liderancaAprovou',
      label: 'Liderança já aprovou a contratação?',
      type: 'select',
      required: true,
      options: [
        { value: 'sim', label: 'Sim' },
        { value: 'pendente', label: 'Pendente de aprovação' },
      ],
    },
    { name: 'anexo', label: 'Currículo, proposta ou documentos (opcional)', type: 'file' },
  ],
  S1_PROCESSOS: [
    { name: 'resumo', label: 'Resumo da customização', type: 'text', required: true },
    { name: 'projetoJira', label: 'Projeto / board Jira', type: 'text', required: true },
    {
      name: 'tipoCustomizacao',
      label: 'Tipo de customização',
      type: 'select',
      required: true,
      options: [
        { value: 'campo', label: 'Campo / formulário' },
        { value: 'workflow', label: 'Workflow / status' },
        { value: 'tela', label: 'Tela / layout' },
        { value: 'integracao', label: 'Integração' },
      ],
    },
    { name: 'descricao', label: 'Descrição detalhada', type: 'textarea', required: true },
    { name: 'impacto', label: 'Impacto esperado', type: 'textarea' },
    { name: 'anexo', label: 'Prints ou exemplos', type: 'file' },
  ],
  S2_ENCERRAMENTO: [
    { name: 'resumo', label: 'Resumo da automação', type: 'text', required: true },
    { name: 'sistemas', label: 'Sistemas envolvidos', type: 'text', required: true },
    { name: 'descricao', label: 'Descrição da demanda', type: 'textarea', required: true },
    { name: 'prazo', label: 'Prazo desejado', type: 'date' },
    { name: 'anexo', label: 'Exemplo / mockup (opcional)', type: 'file' },
  ],
  S3_2_VIA: [
    { name: 'solicitante', label: 'Solicitante', type: 'text', required: true },
    { name: 'sistema', label: 'Sistema / ferramenta', type: 'text', required: true },
    { name: 'tipoAcesso', label: 'Tipo de acesso', type: 'select', required: true, options: [
      { value: 'novo', label: 'Novo acesso' },
      { value: 'revogar', label: 'Revogar acesso' },
      { value: 'alterar', label: 'Alterar permissões' },
    ]},
    { name: 'justificativa', label: 'Justificativa', type: 'textarea', required: true },
    { name: 'anexo', label: 'Prints ou credenciais (opcional)', type: 'file' },
  ],
  S4_SOFTWARE: [
    { name: 'resumo', label: 'Nome do relatório', type: 'text', required: true },
    { name: 'descricao', label: 'Descrição / filtros', type: 'textarea', required: true },
    {
      name: 'frequencia',
      label: 'Frequência',
      type: 'select',
      required: true,
      options: [
        { value: 'unico', label: 'Único' },
        { value: 'semanal', label: 'Semanal' },
        { value: 'mensal', label: 'Mensal' },
      ],
    },
    { name: 'destinatarios', label: 'Destinatários do relatório', type: 'text', required: true },
    {
      name: 'formato',
      label: 'Formato de entrega',
      type: 'select',
      options: [
        { value: 'csv', label: 'CSV' },
        { value: 'xlsx', label: 'Excel' },
        { value: 'pdf', label: 'PDF' },
        { value: 'dashboard', label: 'Dashboard Jira' },
      ],
    },
  ],
  S5_SUPORTE: [
    { name: 'resumo', label: 'Resumo do incidente', type: 'text', required: true },
    { name: 'automacao', label: 'Automação afetada', type: 'text', required: true },
    {
      name: 'prioridade',
      label: 'Prioridade',
      type: 'select',
      required: true,
      options: urgencyOptions,
    },
    {
      name: 'ambiente',
      label: 'Ambiente',
      type: 'select',
      required: true,
      options: [
        { value: 'producao', label: 'Produção' },
        { value: 'homologacao', label: 'Homologação' },
      ],
    },
    { name: 'dataIncidente', label: 'Data/hora do incidente', type: 'date', required: true },
    { name: 'descricao', label: 'Descrição do problema', type: 'textarea', required: true },
  ],
  C4_CONTRATO_AGRADA: [
    ...commonEmpresaFields,
    { name: 'summary', label: 'Resumo do contrato', type: 'text', required: true },
    { name: 'description', label: 'Observações', type: 'textarea' },
    { name: 'valorTotal', label: 'Valor total (R$)', type: 'number' },
    { name: 'closerNome', label: 'Closer', type: 'text' },
    { name: 'linkedDealId', label: 'ID do deal (opcional)', type: 'text' },
    { name: 'anexo', label: 'Proposta e documentos', type: 'file', required: true },
  ],
  O1_OUTROS_COMERCIAL: [
    { name: 'assunto', label: 'Assunto', type: 'text', required: true },
    {
      name: 'descricao',
      label: 'Descreva a demanda comercial',
      type: 'textarea',
      required: true,
      placeholder: 'Contexto, cliente envolvido, prazo e o que precisa ser feito.',
    },
    {
      name: 'urgencia',
      label: 'Nível de urgência',
      type: 'select',
      required: true,
      options: urgencyOptions,
    },
    { name: 'anexo', label: 'Anexos (opcional)', type: 'file' },
  ],
}

export const EXTENDED_TYPES_WITH_EMPRESA_PREFILL: ExtendedPortalTypeKey[] = [
  'C0_NOVO_CLIENTE',
  'C1_ATUALIZACAO_CADASTRO',
  'C5_ENCERRAMENTO',
  'C4_CONTRATO_AGRADA',
  'O1_OUTROS_COMERCIAL',
]
